import React from 'react'

// Types
import { CategoryLandingPageNavigationItemType } from '.'

// Components
import Link from 'next/link'
import Icon from '@dustin-web/microsite-components/src/icon'

// Hooks
import { useLayout, useTranslations } from '@dustin-web/microsite-components/src/hooks'

// Utils
import { stripTrailingSlash } from '@dustin-web/microsite-components/src/utils/urls'
import { getCategoryUrl } from '@dustin-web/microsite-components/src/utils/urls'
import { getMainCategoryIcon } from '@dustin-web/microsite-components/src/utils/main-category-icons'
import classNames from 'classnames'

type Props = {
  items: CategoryLandingPageNavigationItemType[]
  isTopCategory?: boolean
  categoryId: string
  itemOnClick?: (selection: string) => void
}
const BARGAIN_CATEGORY_ID = '2029898'

const Categories = ({ items, categoryId, isTopCategory = false, itemOnClick }: Props) => {
  const { responsive } = useLayout()
  const { translate } = useTranslations()

  if (!items) {
    return null
  }
  return responsive.breakpoints.max.SM ? (
    <>
      {isTopCategory && (
        <CategoryLink
          text={translate('Show_Bargains')}
          icon="bargain"
          href={stripTrailingSlash(translate('Bargains_URL'))}
          isTopCategory={true}
          englishCategoryName={'Show Bargains'}
          itemOnClick={itemOnClick}
        />
      )}
      {categoryId === BARGAIN_CATEGORY_ID && (
        <CategoryLink
          icon={'arrow-right-small'}
          text={translate('ShowAll_Bargains')}
          href={`${stripTrailingSlash(translate('Bargains_URL'))}?showonly=instock`}
          isTopCategory={isTopCategory}
          englishCategoryName={'Show All Bargains'}
          itemOnClick={itemOnClick}
        />
      )}
      {items.map(
        (child: CategoryLandingPageNavigationItemType, i: number) =>
          child.category && (
            <CategoryLink
              key={child.categoryId + i}
              text={child.category.seoLongTitleName ?? child.category.name}
              icon={child.categoryId}
              href={getCategoryUrl(child.category.pathSlug)}
              isTopCategory={isTopCategory}
              itemOnClick={itemOnClick}
              englishCategoryName={child.category.englishCategoryName}
            />
          )
      )}
    </>
  ) : (
    <div className="row sm-gutters">
      {isTopCategory && (
        <div className="col-2 u-mb-16">
          <div className="d-block">{getMainCategoryIcon('bargain', 'u-mb-8 i-24 i-dark-gray')}</div>
          <h2 className="h4 d-inline u-relative u-mb-0">
            <Link
              onClick={() => itemOnClick && itemOnClick('Show Bargains')}
              prefetch={false}
              href={stripTrailingSlash(translate('Bargains_URL'))}
              scroll={true}
              className="a-stripped">
              {translate('Show_Bargains')}
            </Link>
            <Icon
              name="arrow-right-small"
              style={{ transform: 'translate(100%, 3px)', right: 0 }}
              className="i-16 u-absolute"
            />
          </h2>
        </div>
      )}
      {categoryId === BARGAIN_CATEGORY_ID && (
        <CategoryBlock
          key={'bargain'}
          hasChildren={false}
          href={`${stripTrailingSlash(translate('Bargains_URL'))}?showonly=instock`}
          isTopCategory={isTopCategory}
          icon={categoryId}
          title={translate('ShowAll_Bargains')}
          child={false}
          itemOnClick={itemOnClick}
          englishCategoryName={'Show All Bargains'}
        />
      )}
      {items.map(
        (child: CategoryLandingPageNavigationItemType, i: number) =>
          child.category && (
            <CategoryBlock
              key={child.categoryId + i}
              hasChildren={child.children && child.children.length > 0}
              href={getCategoryUrl(child.category.pathSlug)}
              isTopCategory={isTopCategory}
              icon={child.categoryId}
              title={child?.category?.seoLongTitleName ?? child.category.name}
              child={child}
              itemOnClick={itemOnClick}
              englishCategoryName={child.category.englishCategoryName}
            />
          )
      )}
    </div>
  )
}

type CategoryCommon = {
  isTopCategory: boolean
  icon: string
  href: string
  englishCategoryName?: string | null
  itemOnClick?: (section: string) => void
}
//

type CategoryBlock = {
  hasChildren: boolean
  title?: string | null
  child: any
}

const CategoryBlock = ({
  isTopCategory,
  icon,
  hasChildren,
  href,
  title,
  child,
  itemOnClick,
  englishCategoryName,
}: CategoryCommon & CategoryBlock) => (
  <div className="col-4 col-lg-2 u-mb-16">
    {isTopCategory && (
      <div className="d-block">{getMainCategoryIcon(icon, 'u-mb-8 i-24 i-dark-gray')}</div>
    )}
    <h2
      className={classNames('h4 d-inline u-relative', {
        'u-mb-0': !hasChildren,
      })}>
      {title ? (
        <Link
          onClick={() => itemOnClick && englishCategoryName && itemOnClick(englishCategoryName)}
          prefetch={false}
          href={href}
          scroll={true}
          className="a-stripped">
          {title}
        </Link>
      ) : null}
      <Icon
        name="arrow-right-small"
        style={{ transform: 'translate(100%, 3px)', right: 0 }}
        className="i-16 u-absolute"
      />
    </h2>

    {hasChildren && (
      <ul className="ul-stripped u-mb-8">
        {child.children.map((grandchild: any, i: number) => (
          <li key={grandchild + i}>
            <Link
              onClick={() => itemOnClick && itemOnClick(grandchild.category?.englishCategoryName)}
              prefetch={false}
              href={getCategoryUrl(grandchild.category?.pathSlug)}
              className="a-stripped u-fs-12"
              scroll={true}>
              {grandchild.category?.seoLongTitleName ?? grandchild.category?.name}
            </Link>
          </li>
        ))}
      </ul>
    )}
  </div>
)

type CategoryLink = {
  text?: string | null
}

const CategoryLink = ({
  href,
  icon,
  isTopCategory,
  text,
  itemOnClick,
  englishCategoryName,
}: CategoryCommon & CategoryLink) => (
  <Link
    onClick={() => itemOnClick && englishCategoryName && itemOnClick(englishCategoryName)}
    prefetch={false}
    href={href}
    className="a-stripped a-img d-block">
    <div
      className={classNames('c-box d-flex align-items-center u-mb-8 u-pr-0 u-pr-8 u-py-12', {
        'u-pl-16': isTopCategory,
      })}>
      {isTopCategory && getMainCategoryIcon(icon, 'i-24 i-dark-gray')}
      {text ? (
        <div className="flex-fill u-pl-8">
          <span className="d-block">{text}</span>
        </div>
      ) : null}
      <div className="d-flex align-items-center u-px-0">
        <Icon name="arrow-right-small" className="i-24" />
      </div>
    </div>
  </Link>
)

export default Categories
